import { useEffect, useState } from "react";
import Header from "./Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfiles } from "../redux/profileSlice";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

function Dashboard() {
  const dispatch = useDispatch();
  const { profiles, loading, error } = useSelector((state) => state.profiles);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [markFilter, setMarkFilter] = useState("All");
  const profilesPerPage = 10;
  const [verificationFilter, setVerificationFilter] = useState("All");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [visibleOtps, setVisibleOtps] = useState({});

  const handleDelete = async (profileId) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this profile?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (confirmResult.isConfirmed) {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `https://xqk5c9abna.execute-api.ap-south-1.amazonaws.com/prod/DeleteProfile?profileId=${profileId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: token,
            },
          }
        );

        if (!res.ok) throw new Error("Delete failed");

        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Profile deleted successfully.",
          timer: 1500,
          showConfirmButton: false,
        });

        dispatch(fetchProfiles());
      } catch (err) {
        Swal.fire("Error", "Something went wrong!", "error");
      }
    }
  };

  const toggleOtpVisibility = (userId) => {
    setVisibleOtps((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchProfiles());
  }, [dispatch]);

  const filteredProfiles = profiles.filter((profile) => {
    const matchesGender =
      markFilter === "All" ||
      profile.gender?.toLowerCase() === markFilter.toLowerCase();

    const matchesVerification =
      verificationFilter === "All" ||
      (verificationFilter === "Verified" && profile.otpVerified) ||
      (verificationFilter === "Not Verified" && !profile.otpVerified);

    const matchesSearch =
      debouncedSearchTerm.trim() === "" ||
      Object.values(profile)
        .concat(Object.values(profile.profile || {}))
        .some((val) =>
          val
            ?.toString()
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase())
        );

    return matchesGender && matchesVerification && matchesSearch;
  });

  const totalPages = Math.ceil(filteredProfiles.length / profilesPerPage);
  const startIndex = (currentPage - 1) * profilesPerPage;
  const currentProfiles = filteredProfiles.slice(
    startIndex,
    startIndex + profilesPerPage
  );

  const handlePrevious = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  useEffect(() => {
    setCurrentPage(1);
  }, [markFilter]);

  return (
    <>
      <Header />
      <div className="container py-3">
        <div className="row g-2">
          <div className="col-8">
            <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
              <table class="table mb-0 text-nowrap table-centered">
                <thead class="bg-dark">
                  <tr>
                    <th className="w-10 text-white">No.</th>
                    <th className="lh-1 w-60 text-white">
                      Details
                      <i className="bi bi-info-circle ms-2 text-white"></i>
                    </th>
                    <th className="w-30 text-white">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProfiles.map((profile, index) => (
                    <tr
                      key={startIndex + index}
                      className={
                        index % 2 === 0 ? "bg-white-light" : "bg-white-dark"
                      }
                    >
                      <td>{startIndex + index + 1}.</td>
                      <td className="lh-sm py-4">
                        <span class="text-dark">User Id</span> &mdash;{" "}
                        {profile.userId}
                        <br />
                        <span class="text-dark">Profile For</span> &mdash;{" "}
                        {profile.profileFor}
                        <br />
                        <span class="text-dark">Gender</span> &mdash;{" "}
                        {profile.gender}
                        <br />
                        <span class="text-dark">Platform</span> &mdash;{" "}
                        {profile.platform}
                        <br />
                        <span class="text-dark">Name</span> &mdash;{" "}
                        {profile.name}
                        <br />
                        <span class="text-dark">Email</span> &mdash;{" "}
                        {profile.email} <br />
                        <span class="text-dark">Mobile</span> &mdash;{" "}
                        {profile.mobile}
                        <br />
                        <span className="text-dark me-1">OTP</span> &mdash;{" "}
                        {visibleOtps[profile.userId] ? profile.otp : "••••"}
                        <i
                          className={`bi ms-2 cursor-pointer ${
                            visibleOtps[profile.userId]
                              ? "bi-eye-slash"
                              : "bi-eye"
                          }`}
                          onClick={() => toggleOtpVisibility(profile.userId)}
                        ></i>
                        <br />
                        <br />
                        <span
                          className={`badge rounded-pill px-3 py-1 ${
                            profile.otpVerified
                              ? "bg-success-subtle text-success"
                              : "bg-danger-subtle text-danger"
                          }`}
                        >
                          {profile.otpVerified ? "Verified" : "Not Verified"}
                        </span>
                        <hr className="w-25" />
                        <span class="text-dark">Age</span> &mdash;{" "}
                        {Math.floor(
                          (Date.now() - profile.profile.dob) /
                            (1000 * 60 * 60 * 24 * 365.25)
                        )}{" "}
                        Years
                        <br />
                        <span class="text-dark">Height</span> &mdash;{" "}
                        {profile.profile.height}
                        <br />
                        <span class="text-dark">
                          Marital Status
                        </span> &mdash; {profile.profile.maritalStatus}
                        <br />
                        <span class="text-dark">Religion</span> &mdash;{" "}
                        {profile.profile.religion}
                        <br />
                        <span class="text-dark">
                          Mother Tongue
                        </span> &mdash; {profile.profile.motherTongue}
                        <br />
                        <span class="text-dark">Community</span> &mdash;{" "}
                        {profile.profile.community}
                        <br />
                        <span class="text-dark">Diet</span> &mdash;{" "}
                        {profile.profile.diet}
                        <br />
                        <span class="text-dark">State</span> &mdash;{" "}
                        {profile.profile.state}
                        <br />
                        <span class="text-dark">City</span> &mdash;{" "}
                        {profile.profile.city}
                        <br />
                        <span class="text-dark">Pincode</span> &mdash;{" "}
                        {profile.profile.pincode}
                        <br />
                        <span class="text-dark">
                          Qualification
                        </span> &mdash; {profile.profile.qualification}
                        <br />
                        <span class="text-dark">Profession</span> &mdash;{" "}
                        {profile.profile.profession}
                        <br />
                        <span class="text-dark">Working In</span> &mdash;{" "}
                        {profile.profile.workingIn}
                        <br />
                        <span class="text-dark">
                          Annual Income
                        </span> &mdash; {profile.profile.annualIncome}
                        <br />
                        <span class="text-dark">Family Type</span> &mdash;{" "}
                        {profile.profile.familyType}
                        <br />
                        <span class="text-dark">
                          Father Status
                        </span> &mdash; {profile.profile.fatherStatus}
                        <br />
                        <span class="text-dark">
                          Mother Status
                        </span> &mdash; {profile.profile.motherStatus}
                        <br />
                        <span class="text-dark">Created At</span> &mdash;{" "}
                        {new Date(profile.createdAt).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </td>
                      <td>
                        <Link
                          to="/update-profile"
                          state={{ profile }}
                          class="badge bg-success rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Update
                        </Link>
                        {profile.images?.length > 0 && (
                          <Link
                            to="/update-image"
                            state={{ profile }}
                            className="badge bg-info rounded-3 px-2 py-2 me-1 mt-1 cursor-pointer"
                          >
                            <i className="bi bi-image"></i>
                          </Link>
                        )}
                        <div
                          onClick={() => handleDelete(profile._id)}
                          class="badge bg-danger rounded-3 px-2 py-2 me-1 mt-1 cursor-pointer"
                        >
                          <i className="bi bi-trash2"></i>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-4">
            <div className="bg-danger-soft rounded-4 p-5">
              <div className="bg-white rounded-pill mb-3">
                <div className="d-flex align-items-center">
                  <span className="position-absolute ps-3 search-icon">
                    <i className="bi bi-filter-circle-fill ms-1" />
                  </span>
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control ps-7 rounded-3 border-0 rounded-pill"
                    placeholder="Filter Data"
                  />
                </div>
              </div>

              {profiles.length === 0 && <div>No profiles found.</div>}

              <h3 className="m-0 lh-sm">All Profiles</h3>
              <p>Users Who’ve Signed up so far</p>
              <p className="fw-medium text-dark mb-2">
                {filteredProfiles.length} {markFilter} Profile
                {filteredProfiles.length !== 1 ? "s" : ""}
              </p>

              <div className="gap-2 d-flex flex-wrap mt-2">
                {["All", "Male", "Female"].map((gender) => (
                  <span
                    key={gender}
                    onClick={() => setMarkFilter(gender)}
                    className={`btn btn-tag btn-sm ${
                      markFilter === gender
                        ? `text-${
                            gender === "Female" ? "danger" : "success"
                          }-emphasis bg-${
                            gender === "Female" ? "danger" : "success"
                          }-subtle border border-${
                            gender === "Female" ? "danger" : "success"
                          }`
                        : ""
                    }`}
                  >
                    {gender}
                  </span>
                ))}
              </div>

              <div className="gap-2 d-flex flex-wrap mt-2">
                {["All", "Verified", "Not Verified"].map((status) => (
                  <span
                    key={status}
                    onClick={() => setVerificationFilter(status)}
                    className={`btn btn-tag btn-sm ${
                      verificationFilter === status
                        ? `text-${
                            status === "Verified"
                              ? "success"
                              : status === "Not Verified"
                              ? "danger"
                              : "secondary"
                          }-emphasis bg-${
                            status === "Verified"
                              ? "success"
                              : status === "Not Verified"
                              ? "danger"
                              : "secondary"
                          }-subtle border border-${
                            status === "Verified"
                              ? "success"
                              : status === "Not Verified"
                              ? "danger"
                              : "secondary"
                          }`
                        : ""
                    }`}
                  >
                    {status}
                  </span>
                ))}
              </div>

              {loading && (
                <div className="mt-2">
                  <div
                    class="spinner-border mt-2 text-dark"
                    role="status"
                  ></div>
                </div>
              )}
              {error && <div className="text-danger">{error}</div>}

              <hr className="border-light" />

              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-dark rounded-4"
                  disabled={currentPage === 1}
                  onClick={handlePrevious}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <span className="text-dark">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-outline-dark rounded-4"
                  disabled={currentPage === totalPages}
                  onClick={handleNext}
                >
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
